import {
	type StylableProps,
	type TransitionStatus,
	NestedTransition,
	StackingContext,
	Transition,
} from "@mdotm/shadcn/legacy/components";
import { type NodeOrFn, overrideClassList, renderNodeOrFn, toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { createPortal } from "react-dom";
import { type CSSProperties, useMemo } from "react";
import { useAppDrawerWidth } from "../AppDrawer/AppDrawer-shared";
import type { SidebarPositions } from "./Sidebar-shared";

export type SidebarProps = {
	open: boolean;
	onClose?(): void;
	width: CSSProperties["width"];
	children: NodeOrFn<SidebarChildrenProps>;
	onAnimationStateChange?(state: TransitionStatus): void;
	mode?: "panel" | "modal";
	position?: SidebarPositions;
	onChangePosition?(position: SidebarPositions): void;
	/** @default true */
	unmountOnExit?: boolean;
} & StylableProps;

export type SidebarChildrenProps = {
	open: boolean;
	onClose?(): void;
	width: CSSProperties["width"];
	position: SidebarPositions;
	setPosition?(newPosition: SidebarPositions): void;
};

export function Sidebar({
	open,
	onClose,
	width,
	children,
	onAnimationStateChange,
	className,
	style,
	mode = "panel",
	position = "right",
	onChangePosition,
	unmountOnExit = true,
}: SidebarProps): React.JSX.Element {
	const childrenProps = useMemo<SidebarChildrenProps>(
		() => ({
			open,
			width,
			onClose,
			position,
			setPosition: onChangePosition,
		}),
		[onChangePosition, onClose, open, position, width],
	);

	const leftOffsett = useAppDrawerWidth();
	return (
		<StackingContext.Consumer>
			{({ zIndex }) =>
				createPortal(
					<Transition onChange={onAnimationStateChange} in={open} unmountOnExit={false} duration={300}>
						{/* overlay */}
						{mode === "modal" && (
							<NestedTransition
								className="fixed inset-0 z-0 bg-black/20 transition-opacity duration-300"
								enterFromClassName="opacity-0"
								enterToClassName="opacity-100"
								unmountOnExit
								style={{
									zIndex,
								}}
							>
								{({ className: animatedClassList, style: animatedStyle, transitionStatus }) => (
									<button
										type="button"
										aria-label="Close"
										tabIndex={-1}
										data-transitionstatus={transitionStatus}
										className={toClassName(animatedClassList)}
										style={animatedStyle}
										onClick={() => onClose?.()}
									/>
								)}
							</NestedTransition>
						)}
						<NestedTransition
							className={overrideClassList(
								"fixed top-0 bottom-0 max-w-full transition-all ease-in-out duration-300 flex flex-col",
								className,
							)}
							// enterFromClassName="-translate-y-full scale-x-[2]" // vertical
							// enterToClassName="translate-y-0 scale-x-[1] shadow-xl"
							enterFromClassName={position === "right" ? "translate-x-full" : "-translate-x-full"} // horizontal
							enterToClassName="translate-x-0 shadow-xl" // horizontal
							style={{
								zIndex,
								width,
								top: 0,
								left: position === "left" ? leftOffsett : undefined,
								right: position === "right" ? 0 : undefined,
								...style,
							}}
							exitedClassName="invisible"
							unmountOnExit={unmountOnExit}
						>
							{({ className: animatedClassList, style: animatedStyle, transitionStatus }) => (
								<div
									data-transitionstatus={transitionStatus}
									className={toClassName(animatedClassList)}
									style={animatedStyle}
								>
									<StackingContext.Provider value={{ zIndex: zIndex + 1 }}>
										{renderNodeOrFn(children, childrenProps)}
									</StackingContext.Provider>
								</div>
							)}
						</NestedTransition>
					</Transition>,
					document.body,
				)
			}
		</StackingContext.Consumer>
	);
}
